import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, GitCompare, ExternalLink, FileSpreadsheet } from "lucide-react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchCompare, compareXlsxUrl, inr } from "@/lib/api";

const primaryProc = (a) => {
  const ps = a.manufacturing_processes || [];
  const p = ps.find((x) => x.primary) || ps[0];
  return p?.process || "—";
};

const ROWS = [
  { label: "Region / Pincode", get: (d) => `${d.analysis?.cost_estimate?.region || ""} ${d.pincode}`.trim() },
  { label: "Annual requirement", get: (d) => d.quantity },
  { label: "Material", get: (d) => `${d.analysis?.material?.specification || ""} ${d.analysis?.material?.grade || ""}`.trim() || "—" },
  { label: "Primary process", get: (d) => primaryProc(d.analysis || {}), highlight: true },
  { label: "Full route", get: (d) => (d.analysis?.manufacturing_processes || []).map((p) => p.process).join(" → ") || "—" },
  { label: "Heat treatment", get: (d) => (d.analysis?.heat_treatment?.required ? d.analysis.heat_treatment.process : "None") },
  { label: "Surface treatment", get: (d) => (d.analysis?.surface_treatment?.required ? d.analysis.surface_treatment.process : "None") },
  { label: "Surface finish", get: (d) => d.analysis?.surface_finish || "—" },
  { label: "Cost / unit", get: (d) => `${inr(d.analysis?.cost_estimate?.per_unit_min)} – ${inr(d.analysis?.cost_estimate?.per_unit_max)}`, mono: true, highlight: true },
  { label: "Total cost", get: (d) => `${inr(d.analysis?.cost_estimate?.total_min)} – ${inr(d.analysis?.cost_estimate?.total_max)}`, mono: true },
  { label: "Tooling items", get: (d) => (d.analysis?.tooling || []).length },
  { label: "Fixtures", get: (d) => (d.analysis?.fixtures || []).length },
  { label: "Machinery", get: (d) => (d.analysis?.machinery || []).length },
  { label: "GD&T callouts", get: (d) => (d.analysis?.gdt || []).length },
  { label: "Confidence", get: (d) => d.analysis?.confidence || "medium" },
];

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const ids = params.get("ids") || "";

  useEffect(() => {
    fetchCompare(ids).then(setDocs).catch(() => setDocs([]));
  }, [ids]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <button
          data-testid="compare-back"
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <GitCompare className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl font-black tracking-tight">Compare analyses</h1>
          </div>
          {docs && docs.length >= 2 && (
            <a
              href={compareXlsxUrl(ids)}
              data-testid="compare-export-excel"
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-border bg-card px-3 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary"
            >
              <FileSpreadsheet className="h-4 w-4" /> Export Excel
            </a>
          )}
        </div>

        {docs === null ? (
          <p className="font-mono text-sm text-muted-foreground">Loading…</p>
        ) : docs.length < 2 ? (
          <div data-testid="compare-insufficient" className="rounded-sm border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">
              Select at least two analyses from the home page to compare them side by side.
            </p>
            <Button className="mt-5 rounded-sm" onClick={() => navigate("/")}>Go to analyses</Button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-border" data-testid="compare-table">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-[160px] border-b border-r border-border bg-card p-4 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Parameter
                  </th>
                  {docs.map((d) => (
                    <th key={d.id} className="min-w-[220px] border-b border-r border-border bg-card p-4 text-left align-top">
                      <Link to={`/analysis/${d.id}`} className="group inline-flex items-start gap-1.5 font-title text-base font-semibold leading-snug hover:text-primary">
                        {d.analysis?.part_name || d.pdf_filename}
                        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-40 group-hover:opacity-100" />
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row, ri) => (
                  <tr key={row.label} className={ri % 2 ? "bg-muted/20" : ""}>
                    <td className="sticky left-0 z-10 border-r border-border bg-inherit p-4 font-medium">
                      {row.label}
                    </td>
                    {docs.map((d) => {
                      const val = row.get(d);
                      return (
                        <td key={d.id} className={`border-r border-border p-4 ${row.mono ? "font-mono" : ""} ${row.highlight ? "font-semibold text-primary" : ""}`}>
                          {row.label === "Confidence" ? (
                            <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{String(val)}</Badge>
                          ) : (
                            String(val)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
